/**
 * ============================================================================
 * 동기화 서비스 (Sync Service)
 * ============================================================================
 * 
 * 주요 기능 요약
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. createSyncRoom   - 동기화 소켓 생성 (교수자 전용)
 * 2. resolveSyncRoom  - 동기화 소켓 연결 정보 조회 (학생용)
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * API 엔드포인트
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * POST /viewer/chat/rooms - 동기화 소켓 생성 (교수자)
 * POST /viewer/chat/rooms/resolve - 동기화 소켓 연결 정보 조회 (학생)
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 사용 예시
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * // 교수자: 동기화 소켓 생성
 * const result = await syncService.createSyncRoom({
 *   bookUuid: 'book-uuid-here',
 *   title: '1장 동기화 수업',
 * });
 * console.log(result.joinCode); // "7506"
 * console.log(result.wsUrl);    // WebSocket URL
 * 
 * // 학생: 동기화 소켓 연결 정보 조회
 * const room = await syncService.resolveSyncRoom('7506');
 * console.log(room.wsUrl); // WebSocket URL
 * 
 * ============================================================================
 */

import apiClient from '../../core/http/axios';

// ============================================================================
// 타입 정의
// ============================================================================

interface CreateSyncRoomRequest {
  bookUuid: string;
  title?: string;
  joinCodeTtlMinutes?: number;
  joinCodeMaxUses?: number;
}

// 교수자용: 동기화 소켓 생성 응답 (클라이언트 타입)
interface SyncRoomData {
  roomUuid: string;
  wsUrl: string;
  bookNo: number;
  title: string;
  joinCode: string;
  expiresAt: string;
}

// 학생용: 동기화 소켓 연결 정보 응답 (클라이언트 타입)
interface SyncResolveData {
  bookUuid: string;
  roomUuid: string;
  wsUrl: string;
  expiresAt: string;
}

// API 응답 타입 (snake_case) - 교수자용
interface ApiSyncRoomData {
  room_uuid: string;
  ws_url: string;
  book_no: number;
  title: string;
  join_code: string;
  expires_at: string;
}

// API 응답 타입 (snake_case) - 학생용
interface ApiSyncResolveData {
  book_uuid: string;
  room_uuid: string;
  ws_url: string;
  expires_at: string;
}

interface CreateSyncRoomResponse {
  success: boolean;
  error: string | null;
  data: ApiSyncRoomData;
  code: string | null;
}

interface ResolveSyncRoomResponse {
  success: boolean;
  error: string | null;
  data: ApiSyncResolveData;
  code: string | null;
}

// 열린 방 목록 조회 응답 타입
interface OpenRoomItem {
  book_uuid: string;
  room_uuid: string;
  status: string;
  ws_url: string;
  created_at: string;
}

interface GetMyOpenRoomsResponse {
  success: boolean;
  error: string | null;
  data: {
    items: OpenRoomItem[];
  };
  code: string | null;
  meta?: {
    elapsed_ms?: number;
  };
}

// ============================================================================
// 동기화 서비스
// ============================================================================

const syncService = {
  /**
   * 동기화 소켓 생성 (교수자 전용)
   * 
   * @param request - 소켓 생성 요청 데이터
   * @returns 생성된 소켓 정보 (join_code, ws_url 포함)
   */
  createSyncRoom: async (request: CreateSyncRoomRequest): Promise<SyncRoomData> => {
    // console.log('[Sync] 동기화 소켓 생성 요청:', request);
    
    try {
      const response = await apiClient.post<CreateSyncRoomResponse>('/viewer/chat/rooms', {
        book_uuid: request.bookUuid,
        title: request.title || '동기화 수업',
        join_code_ttl_minutes: request.joinCodeTtlMinutes || 60,
        join_code_max_uses: request.joinCodeMaxUses || 1000,
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || '동기화 소켓 생성 실패');
      }
      
      const data = response.data.data;
      // console.log('[Sync] 동기화 소켓 생성 성공:', {
      //   roomUuid: data.room_uuid,
      //   joinCode: data.join_code,
      //   wsUrl: data.ws_url,
      // });
      
      // snake_case -> camelCase 변환
      return {
        roomUuid: data.room_uuid,
        wsUrl: data.ws_url,
        bookNo: data.book_no,
        title: data.title,
        joinCode: data.join_code,
        expiresAt: data.expires_at,
      };
    } catch (error) {
      // console.error('[Sync] 동기화 소켓 생성 실패:', error);
      throw error;
    }
  },

  /**
   * 동기화 소켓 연결 정보 조회 (학생용)
   * 
   * @param joinCode - 4자리 참가 코드
   * @returns 소켓 연결 정보 (WebSocket URL 포함)
   */
  resolveSyncRoom: async (joinCode: string): Promise<SyncResolveData> => {
    // console.log('[Sync] 동기화 소켓 연결 정보 조회 요청:', { joinCode });
    
    try {
      const response = await apiClient.post<ResolveSyncRoomResponse>('/viewer/chat/rooms/resolve', {
        join_code: joinCode,
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || '동기화 소켓 연결 정보 조회 실패');
      }
      
      const data = response.data.data;
      // console.log('[Sync] 동기화 소켓 연결 정보 조회 성공:', {
      //   bookUuid: data.book_uuid,
      //   roomUuid: data.room_uuid,
      //   wsUrl: data.ws_url,
      // });
      
      // snake_case -> camelCase 변환
      return {
        bookUuid: data.book_uuid,
        roomUuid: data.room_uuid,
        wsUrl: data.ws_url,
        expiresAt: data.expires_at,
      };
    } catch (error) {
      // console.error('[Sync] 동기화 소켓 연결 정보 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 내가 생성한 열린 방 목록 조회
   * 
   * @returns 열린 방 목록
   */
  getMyOpenRooms: async (): Promise<OpenRoomItem[]> => {
    // console.log('[Sync] 내가 생성한 열린 방 목록 조회 요청');
    
    try {
      const response = await apiClient.get<GetMyOpenRoomsResponse>('/viewer/chat/rooms/my/open');
      
      if (!response.data.success) {
        throw new Error(response.data.error || '열린 방 목록 조회 실패');
      }
      
      const items = response.data.data.items || [];
      // console.log('[Sync] 열린 방 목록 조회 성공:', {
      //   totalCount: items.length,
      //   items,
      // });
      
      return items;
    } catch (error) {
      // console.error('[Sync] 열린 방 목록 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 동기화 소켓 닫기 (교수자 전용)
   * 
   * @param roomUuid - 닫을 방의 UUID
   */
  closeSyncRoom: async (roomUuid: string): Promise<void> => {
    // console.log('[Sync] 동기화 소켓 닫기 요청:', roomUuid);
    
    try {
      const response = await apiClient.post(`/viewer/chat/rooms/${roomUuid}/close`, {});
      
      if (!response.data.success) {
        throw new Error(response.data.error || '동기화 소켓 닫기 실패');
      }
      
      // console.log('[Sync] 동기화 소켓 닫기 성공:', roomUuid);
    } catch (error) {
      // console.error('[Sync] 동기화 소켓 닫기 실패:', error);
      throw error;
    }
  },
};

export default syncService;
export type { CreateSyncRoomRequest, SyncRoomData, SyncResolveData, OpenRoomItem };
